import { useState } from 'react';

/**
 * Reusable period selector for KPI / report views.
 *
 * Emits `{ kind, start, end }` to onChange when the user picks a preset
 * or commits a custom range. `start`/`end` are ISO strings the server
 * can pass straight into a SQL date comparison; `kind` is the preset
 * key (mtd / last_month / ytd / custom) so consumers can show context
 * labels like "Month-to-date".
 *
 * Why presets are computed client-side AND server-side: the server
 * accepts `?period=mtd` so a refresh on a permalinked URL still works
 * without the client having to recompute boundaries. The client emits
 * the dates anyway so the URL/search params stay self-describing.
 */
const PRESETS = [
  { kind: 'mtd', label: 'Month to Date' },
  { kind: 'last_month', label: 'Last Month' },
  { kind: 'ytd', label: 'Year to Date' },
  { kind: 'custom', label: 'Custom' },
];

function presetRange(kind) {
  const now = new Date();
  const utc = (y, m, d, h = 0, mi = 0, s = 0, ms = 0) =>
    new Date(Date.UTC(y, m, d, h, mi, s, ms)).toISOString();
  if (kind === 'mtd') {
    return { start: utc(now.getUTCFullYear(), now.getUTCMonth(), 1), end: now.toISOString() };
  }
  if (kind === 'last_month') {
    return {
      start: utc(now.getUTCFullYear(), now.getUTCMonth() - 1, 1),
      end: utc(now.getUTCFullYear(), now.getUTCMonth(), 1),
    };
  }
  if (kind === 'ytd') {
    return { start: utc(now.getUTCFullYear(), 0, 1), end: now.toISOString() };
  }
  return { start: null, end: null };
}

export default function PeriodSelector({ value, onChange, compact = false }) {
  const kind = value?.kind || 'mtd';
  const [customStart, setCustomStart] = useState(value?.start?.slice(0, 10) || '');
  const [customEnd, setCustomEnd] = useState(value?.end?.slice(0, 10) || '');

  const pickPreset = (k) => {
    if (k === 'custom') {
      onChange({ kind: 'custom', start: customStart || null, end: customEnd || null });
    } else {
      const r = presetRange(k);
      onChange({ kind: k, start: r.start, end: r.end });
    }
  };

  const commitCustom = () => {
    if (!customStart || !customEnd) return;
    // Widen to full-day boundaries so YYYY-MM-DD picks up the full day
    const s = `${customStart}T00:00:00.000Z`;
    const e = `${customEnd}T23:59:59.999Z`;
    onChange({ kind: 'custom', start: s, end: e });
  };

  return (
    <div style={{
      display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
      padding: compact ? '6px 0' : '10px 0',
    }}>
      <div style={{ display: 'flex', gap: 4, padding: 3, backgroundColor: '#f3f4f6', borderRadius: 8 }}>
        {PRESETS.map(p => {
          const active = kind === p.kind;
          return (
            <button
              key={p.kind}
              onClick={() => pickPreset(p.kind)}
              className="btn btn-sm"
              style={{
                fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 6,
                backgroundColor: active ? '#1a1a2e' : 'transparent',
                color: active ? '#D4A843' : '#6b7280',
                border: 'none',
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>
      {kind === 'custom' && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <input
            type="date"
            value={customStart}
            onChange={e => setCustomStart(e.target.value)}
            onBlur={commitCustom}
            className="sales-input"
            style={{ padding: '6px 10px', fontSize: 13 }}
          />
          <span style={{ color: '#9ca3af' }}>→</span>
          <input
            type="date"
            value={customEnd}
            onChange={e => setCustomEnd(e.target.value)}
            onBlur={commitCustom}
            className="sales-input"
            style={{ padding: '6px 10px', fontSize: 13 }}
          />
        </div>
      )}
    </div>
  );
}
